import React, { useEffect } from 'react';
import DocumentRequested from './Document-requested';
import CustomSidebarComponent from './CustomSidebarComponent';
import { useAuth, useDocumentListContext } from '../../utils/hooks';
import { useSearchParams } from 'react-router-dom';
import { Loader } from '../../components/Loader';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import DocumentRequestModal from './DocumentRequestModal';
import styles from './document-requested.module.scss';
import RequestIcon from '../../assets/Icons/RequestIcon';

export default function Documentrequestedmain() {
  const [qParams] = useSearchParams();
  const {
    useGetDocumentRequestList,
    useGetRequestQuestionList,
    mutateDocumentSave,
    isTicketDocumentSaveLoading,
    openModal,
    isClientUpdateAnswerLoading,
  } = useDocumentListContext();
  const { useGetConfigData } = useAuth();

  const token = qParams.get('token');
  const organizationCode = qParams.get('code');

  const { data: userConfigData, isLoading: isUserConfigDataLoading } =
    useGetConfigData(organizationCode);

  const {
    data: documentRequestList,
    isLoading,
    isRefetching,
  } = useGetDocumentRequestList(token);

  const { data: requestQuestionList, isLoading: isQuestionListLoading } =
    useGetRequestQuestionList(token);

  const { handleSubmit, control, reset } = useForm({
    mode: 'all',
  });

  /**
   * useEffect hook that resets the ticketIDRDocuments field with the list of documents that have not been received.
   * @param {Function} reset - Function to reset the form fields.
   * @param {Object} documentRequestList - The list of document requests.
   * @returns None
   */
  useEffect(() => {
    reset({
      ticketIDRDocuments:
        documentRequestList?.doclist?.filter((doc) => !doc.received) || [],
      idrQuestions:
        requestQuestionList?.answers?.map((item) => ({
          ...item,
          answers: item.answers || '',
        })) || [],
    });
  }, [documentRequestList, requestQuestionList]);

  const { fields: documentList } = useFieldArray({
    control,
    name: 'ticketIDRDocuments',
  });

  const { fields: questionList } = useFieldArray({
    control,
    name: 'idrQuestions',
  });

  /**
   * Saves the uploaded documents based on the provided data.
   * @param {{object}} data - The data containing information about the uploaded documents.
   * @returns None
   */
  const onSave = (data) => {
    const filterUploadedDocument =
      data.ticketIDRDocuments?.map((documentType) => ({
        ticketDocumentID: documentType.ticketDocumentID || 0,
        received: documentType.received,
        isActive: true,
        status: 'Active',
        document: documentType.multipleDocumentList
          .filter((document) => !document.ticketIDRMultipleDocumentID)
          .map((document) => ({
            ...document,
            ticketIDRMultipleDocumentID: 0,
            isActive: true,
          })),
      })) || [];

    const finalData = {
      token,
      list:
        filterUploadedDocument?.filter(
          (documentType) => documentType.document.length > 0
        ) || [],
      answers: {
        answers: data.idrQuestions?.map((item) => ({
          ...item,
          answered: !!item.answers,
          isActive: true,
        })),
      },
    };

    // Determine what type of requests exist in the ticket
    const hasDocumentRequests = data.ticketIDRDocuments && data.ticketIDRDocuments.length > 0;
    const hasQuestionRequests = data.idrQuestions && data.idrQuestions.length > 0;

    // Check user actions
    const hasNewDocument = finalData.list.length > 0;
    const hasAnsweredQuestion = Array.isArray(finalData.answers.answers)
      ? finalData.answers.answers.some((item) => item.answered)
      : false;

    // Context-aware validation
    let isValid = false;
    let errorMessage = '';

    if (hasDocumentRequests && hasQuestionRequests) {
      // Ticket has both - user must do at least one
      isValid = hasNewDocument || hasAnsweredQuestion;
      errorMessage = 'Please upload at least one new document or answer at least one question';
    } else if (hasDocumentRequests && !hasQuestionRequests) {
      // Ticket has only documents - user must upload at least one document
      isValid = hasNewDocument;
      errorMessage = 'Please upload at least one new document';
    } else if (!hasDocumentRequests && hasQuestionRequests) {
      // Ticket has only questions - user must answer at least one question
      isValid = hasAnsweredQuestion;
      errorMessage = 'Please answer at least one question';
    } else {
      // No requests at all - shouldn't happen but handle it
      isValid = false;
      errorMessage = 'No document or question requests found';
    }

    if (isValid) {
      mutateDocumentSave(finalData);
    } else {
      toast.error(errorMessage);
    }
  };

  if (
    isLoading ||
    isRefetching ||
    isQuestionListLoading ||
    isUserConfigDataLoading
  ) {
    return <Loader />;
  }

  if (!documentRequestList?.success && !isLoading) {
    return (
      <div>
        <DocumentRequestModal
          open
          title="Link Expired"
          message="Please contact the sender to request a new link"
        />
      </div>
    );
  }

  return (
    <div className={styles.documentRequestBody}>
      <CustomSidebarComponent
        logo={userConfigData?.result?.logoURLReference}
        name={userConfigData?.result?.name}
      />
      <div className={styles.headerComponent}>
        <p> {userConfigData?.result?.name} - PBC Request</p>
        <RequestIcon />
      </div>
      <form onSubmit={handleSubmit(onSave)}>
        <DocumentRequested
          isTicketDocumentSaveLoading={
            isTicketDocumentSaveLoading || isClientUpdateAnswerLoading
          }
          documentList={documentList || []}
          questionList={questionList || []}
          control={control}
          organizationCode={organizationCode}
          taxYear={documentRequestList?.taxYear}
        />
      </form>
      {openModal && (
        <DocumentRequestModal
          open={openModal}
          title="Success"
          message="Document request updated successfully"
          type="success"
        />
      )}
    </div>
  );
}
